<template>
    <div>
        <v-container class="fare-seasonality-rules pa-0">
            <div class="fare-seasonality-rules__title color-grey-onfly mb-2">
                Períodos de Sazonalidade
            </div>

            <div class="fare-seasonality-rules__chips mb-3">
                <v-chip
                    v-for="(rule, idx) in value"
                    :key="idx"
                    outlined
                    pill
                    close
                    class="mr-2 mb-2 fare-seasonality-rules__chip"
                    @click:close="remove(idx)"
                >
                    <div class="fare-seasonality-rules__chip-content">
                        <span>{{ formatDate(rule.initial_date) }} — {{ formatDate(rule.final_date) }}</span>
                        <span class="fare-seasonality-rules__chip-nights">
                            {{ nightsCount(rule) }} noite(s)
                            <span
                                class="fare-seasonality-rules__chip-status"
                                :style="{ backgroundColor: periodStatus(rule).color }"
                            >
                                {{ periodStatus(rule).label }}
                            </span>
                        </span>
                    </div>
                </v-chip>
                <span v-if="!value.length" :class="['caption', showError ? 'red--text' : 'color-grey-onfly']">
                    {{ showError ? 'É necessário cadastrar ao menos um período de sazonalidade.' : 'Nenhum período cadastrado.' }}
                </span>
            </div>

            <v-row align="center">
                <div class="fare-seasonality-rules__form-container">
                    <v-menu
                        v-model="menuInitial"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="visibleInitialDate"
                                label="Data Inicial"
                                outlined
                                dense
                                readonly
                                prepend-inner-icon="mdi-calendar"
                                v-bind="attrs"
                                v-on="on"
                                hide-details
                                class="fare-seasonality-rules__form-container-item"
                            />
                        </template>
                        <v-date-picker
                            v-model="newInitialDate"
                            locale="pt-BR"
                            :first-day-of-week="1"
                            :min="today"
                            no-title
                            @input="menuInitial = false"
                        />
                    </v-menu>
                    <v-menu
                        v-model="menuFinal"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                                v-model="visibleFinalDate"
                                label="Data Final"
                                outlined
                                dense
                                readonly
                                prepend-inner-icon="mdi-calendar"
                                v-bind="attrs"
                                v-on="on"
                                hide-details
                                class="fare-seasonality-rules__form-container-item"
                            />
                        </template>
                        <v-date-picker
                            v-model="newFinalDate"
                            locale="pt-BR"
                            :first-day-of-week="1"
                            :min="newInitialDate || today"
                            no-title
                            @input="menuFinal = false"
                        />
                    </v-menu>
                    <v-btn
                        outlined
                        color="primary"
                        :disabled="!newInitialDate || !newFinalDate"
                        @click="add"
                        class="fare-seasonality-rules__form-container-item"
                    >
                        <div class="add-button-text">
                            <v-icon>mdi-plus</v-icon>
                            Adicionar Período
                        </div>
                    </v-btn>
                </div>
            </v-row>

            <v-row v-if="rangeError" no-gutters>
                <v-col>
                    <span class="red--text caption">{{ rangeError }}</span>
                </v-col>
            </v-row>
        </v-container>

        <response-modal
            :show-dialog="conflictDialogVisible"
            type="info"
            title="Existem reservas neste período"
            :message="conflictDialogMessage"
            @close="conflictDialogVisible = false"
        />
    </div>
</template>

<script>
import FareExpirationMixin from "../../../mixins/Fares/FareExpirationMixin";
import ResponseModal from "../../Modals/ResponseModal.vue";

export default {
    name: "FareSeasonalityRules",
    mixins: [FareExpirationMixin],
    components: { ResponseModal },
    props: {
        value: {
            type: Array,
            default: function () { return []; },
        },
        roomId: {
            type: Number,
            default: null,
        },
    },
    data: function () {
        return {
            newInitialDate: null,
            newFinalDate: null,
            menuInitial: false,
            menuFinal: false,
            rangeError: null,
            conflictDialogVisible: false,
            conflictDialogMessage: '',
            showError: false,
        };
    },
    computed: {
        today () {
            const spDate = new Date(Date.now() - 3 * 60 * 60 * 1000);
            return spDate.toISOString().slice(0, 10);
        },
        visibleInitialDate () {
            return this.newInitialDate ? this.formatDateTimeIntoDate(this.newInitialDate) : null;
        },
        visibleFinalDate () {
            return this.newFinalDate ? this.formatDateTimeIntoDate(this.newFinalDate) : null;
        },
    },
    methods: {
        async add () {
            if (!this.isRangeValidated()) {
                return false;
            }

            if (this.roomId) {
                const response = await axios.get('/api/booking/check-room-conflict', {
                    params: {
                        room_id: this.roomId,
                        initial_date: this.newInitialDate,
                        final_date: this.newFinalDate,
                    },
                });

                if (response.data.has_conflict) {
                    this.openBookingConflictDialog(this.newInitialDate, this.newFinalDate);
                }
            }

            this.addPeriodToValue();
            this.resetPeriods();
        },
        addPeriodToValue () {
            const updated = this.value.slice();
            updated.push({
                id: null,
                initial_date: this.newInitialDate,
                final_date: this.newFinalDate,
            });

            this.$emit('input', updated);
        },
        remove (idx) {
            const updated = this.value.slice();
            updated.splice(idx, 1);
            this.$emit('input', updated);
        },
        formatDate (date) {
            return date ? this.formatDateTimeIntoDate(date) : '';
        },
        nightsCount (rule) {
            const diff = new Date(rule.final_date) - new Date(rule.initial_date);
            return Math.round(diff / (1000 * 60 * 60 * 24)) + 1;
        },
        periodStatus (rule) {
            const today = new Date().toISOString().slice(0, 10);
            if (rule.final_date < today) {
                return { label: 'Expirado', color: '#9e9e9e' };
            }
            if (rule.initial_date <= today) {
                return { label: 'Em vigência', color: '#4caf50' };
            }
            return { label: 'Futuro', color: '#1976d2' };
        },
        openBookingConflictDialog (initialDate, finalDate) {
            this.setConflictBookingPeriodMessage(this.formatDateTimeIntoDate(initialDate), this.formatDateTimeIntoDate(finalDate));
            this.conflictDialogVisible = true;
        },
        isRangeValidated () {
            this.rangeError = null;

            if (this.newInitialDate < this.today) {
                this.rangeError = 'A data inicial não pode ser anterior à data atual.';
                return false;
            }

            if (this.newFinalDate < this.today) {
                this.rangeError = 'A data final não pode ser anterior à data atual.';
                return false;
            }

            if (this.newFinalDate < this.newInitialDate) {
                this.rangeError = 'A data final deve ser igual ou posterior à data inicial.';
                return false;
            }

            const hasOverlap = this.value.some(function (rule) {
                return rule.initial_date <= this.newFinalDate && rule.final_date >= this.newInitialDate;
            }, this);

            if (hasOverlap) {
                this.rangeError = 'Este período se sobrepõe a um período já cadastrado.';
                return false;
            }

            return true;
        },
        resetPeriods() {
            this.newInitialDate = null;
            this.newFinalDate = null;
        },
        validate () {
            this.showError = true;
            return this.value.length > 0;
        },
        setConflictBookingPeriodMessage (initialDate, finalDate) {
            this.conflictDialogMessage = `Existe(m) uma ou mais reservas para este quarto entre o período de ${initialDate}
            a ${finalDate}. Caso precise cancelá-la(s), entre em contato com o time de Sourcing.`
        }
    },
};
</script>

<style lang="scss" scoped>
.fare-seasonality-rules {
    padding-top: 1rem;
    margin-top: 1rem;

    &__chip {
        display: inline-block;
        height: 100%;
        padding: 0.375rem 1rem;
        border-radius: 1rem !important;

        :deep(button) {
            margin-left: 0.75rem !important;
            color: rgb(255, 82, 82) !important;
            opacity: 0.75;
        }
    }

    &__title {
        font-size: 0.875rem;
        font-weight: 600;
    }

    &__chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
    }

    &__chip-content {
        display: flex;
        flex-direction: column;
        line-height: 1.3;
        font-weight: 500;
        color: #3B3B3E !important;
    }

    &__chip-nights {
        padding-top: 0.25rem;
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 0.75rem;
        opacity: 0.75;
    }

    &__chip-status {
        display: inline-block;
        padding: 1px 6px;
        border-radius: 999px;
        font-size: 0.65rem;
        font-weight: 600;
        color: #fff;
        letter-spacing: 0.02em;
    }

    button {
        height: 40px !important;

        :deep(.add-button-text) {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
        }
    }

    &__form-container {
        display: flex;
        gap: 0.5rem;
        padding: 0.375rem 0.75rem 0.75rem 0.75rem;
    }

    @media (max-width: 959px) {
        &__form-container {
            flex-direction: column;
        }

        &__form-container {
            width: 100%;
        }
    }
}
</style>
